import React, { useState, useEffect, useMemo } from "react";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { motion, AnimatePresence } from "framer-motion";
import {
  X,
  Menu,
  LayoutDashboard,
  Users,
  FileText,
  Settings,
  // BarChart3,
  FolderOpen,
  Calendar,
  Info,
  Target,
  ChevronDown,
  ChevronRight,
  Megaphone,
  Building2,
  MapPin,
  GraduationCap,
} from "lucide-react";
import { useSelector } from "react-redux";
import type { RootState } from "../../store";
import { hasPermission, hasAnyPermission } from "../../store/auth/authSlice";

interface NavItem {
  name: string;
  href: string;
  icon: React.ComponentType<{ className?: string }>;
  children?: NavItem[];
}

const AdminSidebar: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set());
  const { t } = useTranslation();
  const location = useLocation();
  const { currentLanguage } = useSelector((state: RootState) => state.language);
  const { user } = useSelector((state: RootState) => state.auth);

  useEffect(() => {
    const handleResize = () => {
      setIsOpen(window.innerWidth >= 1024);
    };
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Auto-expand content section if on blog pages
  useEffect(() => {
    if (location.pathname.includes("/admin/blogs")) {
      setExpandedItems((prev) => new Set([...prev, t("sidebar.content")]));
    }
  }, [location.pathname, t]);

  // Filter nav items based on user permissions
  const navItems: NavItem[] = useMemo(() => {
    const items: NavItem[] = [];

    // Dashboard - always visible for authenticated users
    items.push({
      name: t("sidebar.dashboard"),
      href: `/${currentLanguage}/admin/dashboard`,
      icon: LayoutDashboard,
    });

    // Users - check for view users permission
    if (hasPermission(user, "view users")) {
      items.push({
        name: t("sidebar.users"),
        href: `/${currentLanguage}/admin/users`,
        icon: Users,
      });
    }

    // Projects - check for view projects permission
    if (
      hasAnyPermission(user, [
        "view projects",
        "create projects",
        "edit projects",
        "delete projects",
      ])
    ) {
      items.push({
        name: t("sidebar.projects"),
        href: `/${currentLanguage}/admin/projects`,
        icon: FolderOpen,
      });
    }

    // Events - check for view events permission
    if (
      hasAnyPermission(user, [
        "view events",
        "create events",
        "edit events",
        "delete events",
      ])
    ) {
      items.push({
        name: t("sidebar.events"),
        href: `/${currentLanguage}/admin/events`,
        icon: Calendar,
      });
    }

    // Announcements - check for view announcements permission
    if (
      hasAnyPermission(user, [
        "view announcements",
        "create announcements",
        "edit announcements",
        "delete announcements",
      ])
    ) {
      items.push({
        name: t("sidebar.announcements"),
        href: `/${currentLanguage}/admin/announcements`,
        icon: Megaphone,
      });
    }

    // Businesses - check for view businesses permission
    if (
      hasAnyPermission(user, [
        "view businesses",
        "create businesses",
        "edit businesses",
        "delete businesses",
      ])
    ) {
      items.push({
        name: t("sidebar.businesses"),
        href: `/${currentLanguage}/admin/businesses`,
        icon: Building2,
      });
    }

    // Attractions - check for view attractions permission
    if (
      hasAnyPermission(user, [
        "view attractions",
        "create attractions",
        "edit attractions",
        "delete attractions",
      ])
    ) {
      items.push({
        name: t("sidebar.attractions"),
        href: `/${currentLanguage}/admin/attractions`,
        icon: MapPin,
      });
    }

    // Scholarships - check for view scholarships permission
    if (
      hasAnyPermission(user, [
        "view scholarships",
        "create scholarships",
        "edit scholarships",
        "delete scholarships",
      ])
    ) {
      items.push({
        name: t("sidebar.scholarships"),
        href: `/${currentLanguage}/admin/scholarships`,
        icon: GraduationCap,
      });
    }

    // Content (Blogs) - check for view blogs permission
    if (
      hasAnyPermission(user, [
        "view blogs",
        "create blogs",
        "edit blogs",
        "delete blogs",
      ])
    ) {
      items.push({
        name: t("sidebar.blogs"),
        href: `/${currentLanguage}/admin/blogs`,
        icon: FileText,
      });
    }

    // Settings - check for view settings or manage settings permission
    if (hasAnyPermission(user, ["view settings", "manage settings"])) {
      const settingsChildren: NavItem[] = [];

      // About - check for manage settings permission
      if (hasPermission(user, "manage settings")) {
        settingsChildren.push({
          name: t("sidebar.about"),
          href: `/${currentLanguage}/admin/settings/about`,
          icon: Info,
        });
      }

      // Core Values - check for manage settings permission
      if (hasPermission(user, "manage settings")) {
        settingsChildren.push({
          name: t("sidebar.core_values"),
          href: `/${currentLanguage}/admin/settings/core-values`,
          icon: Target,
        });
      }

      // Only show settings if there are children or user has view settings
      if (settingsChildren.length > 0 || hasPermission(user, "view settings")) {
        items.push({
          name: t("sidebar.settings"),
          href: `/${currentLanguage}/admin/settings`,
          icon: Settings,
          children: settingsChildren.length > 0 ? settingsChildren : undefined,
        });
      }
    }

    return items;
  }, [user, currentLanguage, t]);

  const toggleSidebar = () => setIsOpen(!isOpen);

  const toggleExpanded = (itemName: string) => {
    setExpandedItems((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(itemName)) {
        newSet.delete(itemName);
      } else {
        newSet.add(itemName);
      }
      return newSet;
    });
  };

  return (
    <>
      {/* Mobile Overlay */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 z-40 md:hidden"
            onClick={toggleSidebar}
          />
        )}
      </AnimatePresence>

      {/* Sidebar */}
      <motion.aside
        initial={{ x: -300 }}
        animate={{ x: isOpen ? 0 : -300 }}
        transition={{ type: "spring", stiffness: 300, damping: 30 }}
        className={`fixed left-0 h-[calc(100vh-4rem)] bg-white dark:bg-gray-900 shadow-lg z-50 sm:w-64 lg:w-60 xl:w-80 md:relative md:translate-x-0 md:z-auto overflow-y-auto ${
          isOpen ? "block" : "hidden lg:block"
        }`}
      >
        <div className="flex flex-col h-full">
          {/* Header */}
          <div
            className={`sm:flex lg:hidden items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700`}
          >
            <div className="flex items-center gap-2">
              <svg
                viewBox="0 0 48 48"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                className="size-6 text-blue-400"
              >
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M24 4H6V17.3333V30.6667H24V44H42V30.6667V17.3333H24V4Z"
                  fill="currentColor"
                />
              </svg>
              <span
                className={`text-lg font-bold text-gray-900 dark:text-white`}
              >
                {t("admin.title")}
              </span>
            </div>
            <button
              onClick={toggleSidebar}
              className={`p-2 text-gray-400 hover:text-gray-600 dark:hover:text-white md:hidden`}
            >
              <X className="size-5" />
            </button>
          </div>

          {/* Navigation */}
          <nav className="flex-1 px-4 py-6">
            <ul className="space-y-2">
              {navItems.map((item) => {
                const Icon = item.icon;
                const isActive = location.pathname === item.href;
                const hasChildren = item.children && item.children.length > 0;
                const isExpanded = expandedItems.has(item.name);

                return (
                  <li key={item.name}>
                    {hasChildren ? (
                      <div>
                        <button
                          onClick={() => toggleExpanded(item.name)}
                          className={`flex items-center justify-between w-full gap-3 px-3 py-2 rounded-lg transition-colors ${
                            isActive
                              ? "bg-blue-600 text-white"
                              : "text-gray-600 hover:bg-gray-100 hover:text-gray-900 dark:text-gray-300 dark:hover:bg-gray-700 dark:hover:text-white"
                          }`}
                        >
                          <div className="flex items-center gap-3">
                            <Icon className="size-5" />
                            <span>{item.name}</span>
                          </div>
                          {isExpanded ? (
                            <ChevronDown className="size-4" />
                          ) : (
                            <ChevronRight className="size-4" />
                          )}
                        </button>
                        {isExpanded && (
                          <ul className="ml-6 mt-2 space-y-1">
                            {item.children!.map((child) => {
                              const ChildIcon = child.icon;
                              const isChildActive =
                                location.pathname === child.href;
                              return (
                                <li key={child.name}>
                                  <Link
                                    to={child.href}
                                    className={`flex items-center gap-3 px-3 py-2 rounded-lg transition-colors ${
                                      isChildActive
                                        ? "bg-blue-600 text-white"
                                        : "text-gray-600 hover:bg-gray-100 hover:text-gray-900 dark:text-gray-300 dark:hover:bg-gray-700 dark:hover:text-white"
                                    }`}
                                  >
                                    <ChildIcon className="size-4" />
                                    <span>{child.name}</span>
                                  </Link>
                                </li>
                              );
                            })}
                          </ul>
                        )}
                      </div>
                    ) : (
                      <Link
                        to={item.href}
                        className={`flex items-center gap-3 px-3 py-2 rounded-lg transition-colors ${
                          isActive
                            ? "bg-blue-600 text-white"
                            : "text-gray-600 hover:bg-gray-100 hover:text-gray-900 dark:text-gray-300 dark:hover:bg-gray-700 dark:hover:text-white"
                        }`}
                      >
                        <Icon className="size-5" />
                        <span>{item.name}</span>
                      </Link>
                    )}
                  </li>
                );
              })}
            </ul>
          </nav>
        </div>
      </motion.aside>

      {/* Mobile Toggle Button */}
      <button
        onClick={toggleSidebar}
        className={`fixed top-4 left-4 z-50 p-2 rounded-lg shadow-lg lg:hidden bg-white text-gray-900 dark:bg-gray-800 dark:text-white`}
      >
        <Menu className="size-5" />
      </button>
    </>
  );
};

AdminSidebar.displayName = "AdminSidebar";

export default AdminSidebar;
